/**
 * Utility functions for credit sale calculations
 */
import dayjs from 'dayjs';

/**
 * Calculates the total amount with interest
 * @param {number} total - The total amount
 * @param {number} interestRate - The interest rate percentage
 * @returns {number} The total amount with interest
 */
export const calculateTotalWithInterest = (
  total,
  interestRate,
  initialPayment
) => {
  const interestAmount = (total - initialPayment) * (interestRate / 100);
  return total + interestAmount;
};

/**
 * Calculates the payment amount for each installment
 * @param {number} total - The total amount
 * @param {number} initialPayment - The initial payment amount
 * @param {number} interestRate - The interest rate percentage
 * @param {number} numberOfPayments - The number of payments
 * @returns {number} The amount per payment
 */
export const calculatePaymentAmount = (
  total,
  initialPayment,
  interestRate,
  numberOfPayments
) => {
  const remainingAmount = total - initialPayment;
  const interestAmount = remainingAmount * (interestRate / 100);
  const totalWithInterest = remainingAmount + interestAmount;
  return totalWithInterest / numberOfPayments;
};

/**
 * Calculates the late payment amount including late payment interest
 * @param {number} total - The total amount
 * @param {number} initialPayment - The initial payment amount
 * @param {number} interestRate - The interest rate percentage
 * @param {number} latePaymentInterestRate - The late payment interest rate percentage
 * @param {number} numberOfPayments - The number of payments
 * @returns {number} The amount per late payment
 */
export const calculateLatePaymentAmount = (
  total,
  initialPayment,
  interestRate,
  latePaymentInterestRate,
  numberOfPayments
) => {
  const remainingAmount = total - initialPayment;
  const interestAmount =
    remainingAmount * (interestRate / 100) +
    remainingAmount * (latePaymentInterestRate / 100);
  const totalWithInterest = remainingAmount + interestAmount;
  return totalWithInterest / numberOfPayments;
};

/**
 * Calculates late payment interest for a specific installment
 * @param {Object} params
 * @param {Date} params.dueDate - The due date of the payment
 * @param {number} params.baseAmount - The base amount of the payment
 * @param {number} params.latePaymentInterestRate - The late payment interest rate percentage
 * @param {number} params.toleranceDays - Days of tolerance for late payment
 * @returns {number} The late payment interest amount
 */
export const calculateLatePaymentInterest = ({
  dueDate,
  baseAmount,
  latePaymentInterestRate,
  toleranceDays,
}) => {
  const today = dayjs();
  const daysLate = today.diff(dueDate, 'day');
  if (daysLate <= toleranceDays) return 0;
  
  const lateInterestRate = latePaymentInterestRate / 100; // Convert to decimal
  const lateInterest = baseAmount * lateInterestRate * (daysLate / 30); // Monthly rate
  return lateInterest;
};

/**
 * Central credit calculation for all components
 * @param {Object} params
 * @param {number} params.subtotal - The subtotal of the sale
 * @param {number} params.initialPayment - The initial payment
 * @param {number} params.interestRate - The interest rate percentage (simple)
 * @param {number} params.numberOfPayments - The number of payments
 * @param {number} params.latePaymentInterestRate - The late payment interest rate percentage
 * @param {number} params.toleranceDays - Days of tolerance for late payment
 * @returns {Object} All relevant credit values
 */
export function calculateCreditSummary({
  subtotal,
  initialPayment = 0,
  interestRate = 0,
  numberOfPayments = 1,
  latePaymentInterestRate = 0,
  toleranceDays = 0,
}) {
  const amountToCredit = subtotal - initialPayment;
  const interest = amountToCredit * (interestRate / 100);
  const totalWithInterest = amountToCredit + interest;
  const paymentAmount = numberOfPayments > 0 ? totalWithInterest / numberOfPayments : 0;
  const lateInterest = amountToCredit * (latePaymentInterestRate / 100);
  const latePaymentAmount = numberOfPayments > 0 ? (amountToCredit + interest + lateInterest) / numberOfPayments : 0;

  return {
    amountToCredit,
    interest,
    totalWithInterest,
    paymentAmount,
    lateInterest,
    latePaymentAmount,
    numberOfPayments,
    toleranceDays,
    initialPayment,
    interestRate,
    latePaymentInterestRate,
  };
}
